/* eslint-disable jira/i18n/no-export-define-message */
import React, { useEffect, useMemo } from 'react';
import type { RovoConfigProviderEntryProps } from '@jira/ai-rovo_actions-shared-types/src/RovoConfigProviderEntry.types.tsx';
import type { RovoConfiguration } from '@jira/ai-rovo_actions-shared-types/src/RovoConfiguration.types.tsx';
import type { RovoAction } from '@jira/ai-rovo_actions-shared-types/src/RovoAction.types.tsx';
import AiAgentIcon from '@atlaskit/icon/core/ai-agent';
import { defineMessage, type MessageDescriptor, useIntl } from '@atlassian/jira-intl';
import { fg } from '@atlassian/jira-feature-gating';
import { HandleActionType } from '@atlassian/rovo-conversation-actions/types';
import type { SlaConfigProviderEntrypointProps } from './SlaConfigProvider.entrypoint.types.tsx';
import { useServicedeskSlaSkillPrompt } from './useServicedeskSlaSkillPrompt.tsx';

export const createServicedeskSlaPrompt: MessageDescriptor = defineMessage({
	defaultMessage: 'Add SLA',
	description: 'SLA AI prompt for creating a new SLA or timemetric?',
	id: 'ai-rovo.permissions.create-servicedesk-sla',
});

export const updateServicedeskSlaPrompt: MessageDescriptor = defineMessage({
	defaultMessage: 'Modify SLA',
	description: 'SLA AI prompt for updating an SLA or timemetric.',
	id: 'ai-rovo.permissions.update-servicedesk-sla',
});

export const getServicedeskSlaPrompt: MessageDescriptor = defineMessage({
	defaultMessage: 'Explain SLAs in this space',
	description: 'SLA AI prompt for getting all SLAs.',
	id: 'ai-rovo.permissions.get-servicedesk-sla',
});

/**
 * Shared hook that builds SLA route config (getActions + tags).
 * Used by SlaConfigProvider (entrypoint) and AiRovoSlaNudgesProvider so getActions
 * is set as soon as the SLA route mounts, without requiring the entrypoint to load.
 */
export const useSlaConfig = (): RovoConfiguration => {
	const { formatMessage } = useIntl();
	const { formatCreateSlaSkillPrompt, formatEditSlaSkillPrompt, formatGetAllSlaSkillPrompt } =
		useServicedeskSlaSkillPrompt();

	return useMemo<RovoConfiguration>(
		() => ({
			getActions: async () => {
				if (!fg('jmb-4989_add_sla_operations_in_rovo')) {
					return [];
				}

				const actions: RovoAction[] = [
					{
						action: {
							id: 'create-servicedesk-sla',
							title: formatMessage(createServicedeskSlaPrompt),
							staticPrompt: formatCreateSlaSkillPrompt(createServicedeskSlaPrompt),
							icon: () => <AiAgentIcon label="" />,
							handleType: HandleActionType.Skill,
						},
					},
					{
						action: {
							id: 'update-servicedesk-sla',
							title: formatMessage(updateServicedeskSlaPrompt),
							staticPrompt: formatEditSlaSkillPrompt(updateServicedeskSlaPrompt),
							icon: () => <AiAgentIcon label="" />,
							handleType: HandleActionType.Skill,
						},
					},
					{
						action: {
							id: 'get-servicedesk-sla',
							title: formatMessage(getServicedeskSlaPrompt),
							staticPrompt: formatGetAllSlaSkillPrompt(getServicedeskSlaPrompt),
							icon: () => <AiAgentIcon label="" />,
							handleType: HandleActionType.Skill,
						},
					},
				];

				return Promise.all(
					actions.map(async ({ action, entryPointActions }) => {
						await entryPointActions?.load();
						return action;
					}),
				);
			},
			tags: fg('jmb-4989_add_sla_operations_in_rovo')
				? { skillRegistryTags: ['manage-sla'] }
				: { skillRegistryTags: [] },
		}),
		[
			formatMessage,
			formatCreateSlaSkillPrompt,
			formatEditSlaSkillPrompt,
			formatGetAllSlaSkillPrompt,
		],
	);
};

/**
 * Rovo actions - JSM Service Desk SLA settings prompts.
 */
export const SlaConfigProvider = ({ setConfiguration }: SlaConfigProviderEntrypointProps): null => {
	const config = useSlaConfig();

	useEffect(() => {
		// eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
		setConfiguration(config);
	}, [config, setConfiguration]);

	return null;
};

export const SlaConfigProviderEntry = ({
	props: { setConfiguration },
}: RovoConfigProviderEntryProps): React.JSX.Element => (
	<SlaConfigProvider setConfiguration={setConfiguration} />
);

export default SlaConfigProviderEntry;
